import React, { useState, useMemo } from 'react'
import { API_URL } from '../../utils'
import Grid from '../grid'
import { useTranslation } from 'react-i18next'
import FileIcon from './FileIcon'
import { FilePreviewModal, isPreviewable, getPreviewFileType } from '../file-preview'

const FileGrid = ({ rows, columns, ...restProps }) => {
  const { t } = useTranslation()
  const [previewIndex, setPreviewIndex] = useState(null)

  const COLUMNS = [
    { title: t('FileManager_file_name_label'), name: 'filename' },
    { title: t('FileManager_uploaded_by_label'), name: 'uploadedBy', width: 150 },
    { title: t('FileManager_created_label'), name: 'createdAt', width: 160 },
    { title: t('FileManager_size_label'), name: 'size', width: 100 },
  ]

  const _rows = rows
    ? rows.map((f) => ({
        ...f,
        uploadedBy: f.user ? f.user.name : f.bidder ? f.bidder.name : 'system',
      }))
    : []

  const previewableFiles = useMemo(() => {
    return _rows
      .filter((f) => f.mimetype && isPreviewable(f.mimetype))
      .map((f) => ({
        url: `${API_URL}/download/${f.id}`,
        title: f.filename,
        downloadUrl: `${API_URL}/download/${f.id}`,
        type: getPreviewFileType(f.mimetype),
        fileId: f.id,
        fileSize: f.size,
      }))
  }, [_rows])

  const fileIdToPreviewIndex = useMemo(() => {
    const map = new Map()
    let index = 0
    _rows.forEach((f) => {
      if (f.mimetype && isPreviewable(f.mimetype)) {
        map.set(f.id, index)
        index++
      }
    })
    return map
  }, [_rows])

  const handleFileClick = (e, row) => {
    if (row.mimetype && isPreviewable(row.mimetype)) {
      e.preventDefault()
      const index = fileIdToPreviewIndex.get(row.id)
      if (index !== undefined) setPreviewIndex(index)
    }
  }

  return (
    <>
      <Grid
        {...restProps}
        noDataMessage={t('File_manager_No_documents_selected')}
        columns={columns || COLUMNS}
        rows={_rows}
        formatters={[
          {
            formatter: ({ value, row }) => {
              const canPreview = row.mimetype && isPreviewable(row.mimetype)
              return canPreview ? (
                <a
                  className="text-foreground inline-flex items-center gap-1.5 no-underline cursor-pointer"
                  onClick={(e) => handleFileClick(e, row)}
                >
                  <FileIcon filename={row.filename} />
                  <span className="truncate">{value}</span>
                </a>
              ) : (
                <a className="text-foreground inline-flex items-center gap-1.5 no-underline" href={`${API_URL}/download/${row.id}`}>
                  <FileIcon filename={row.filename} />
                  <span className="truncate">{value}</span>
                </a>
              )
            },
            for: ['filename'],
          },
        ]}
        defaultSorting={[{ columnName: 'filename', direction: 'asc' }]}
      />

      {previewableFiles.length > 0 && previewIndex !== null && (
        <FilePreviewModal
          isOpen={true}
          files={previewableFiles}
          currentIndex={previewIndex}
          onNavigate={setPreviewIndex}
          onClose={() => setPreviewIndex(null)}
        />
      )}
    </>
  )
}

export default FileGrid
